import { MFECatalog } from "@sinch/mfe-loader";
import { z } from "zod";
import type { PossiblyIncompleteShellEnv, ShellEnvFromHTML } from "./client";

const mfeConfigSchema = z.object({
  name: z.string(),
  variant: z.string(),
  revision: z.string(),
  flags: z
    .array(z.string())
    .nullish()
    .transform((val) => val ?? []),
});

export type MfeConfig = z.infer<typeof mfeConfigSchema>;

// Const/let statements do not create globals on window, however the variables are invisibly scoped to here.
declare const SHELL_ENV: ShellEnvFromHTML;

// We copy the existing variable so we can refer to the initial config.
const SHELL_ENV_FROM_HTML: ShellEnvFromHTML = Object.assign({}, SHELL_ENV);

const getShellRevision = () => {
  const shellConfig = MFECatalog.mfeConfig["Shell"];
  if (shellConfig?.url.includes(MFECatalog.MFE_CAT_URL)) {
    const revision = shellConfig.url.split("/").pop();
    return revision;
  } else {
    // No revision when running shell locally
    return "";
  }
};

const loadEnvOverrides = async () => {
  const revision = getShellRevision();
  const configName = SHELL_ENV_FROM_HTML.CONFIG_NAME;

  if (!revision) {
    const shellConfig = MFECatalog.mfeConfig["Shell"];
    return await loadEnvFromURL(
      `${shellConfig?.url ?? ""}/dev/${configName}.json`
    );
  }

  const baseUrl = `${SHELL_ENV_FROM_HTML.MFE_CAT_URL}${
    revision ? `/${revision}` : ""
  }`;

  const url = `${baseUrl}/dev/${configName}.json`;
  return await loadEnvFromURL(url);
};

// eslint-disable-next-line no-restricted-syntax
export const loadShellEnv: () => Promise<PossiblyIncompleteShellEnv> =
  async () => {
    const envOverrides = await loadEnvOverrides().catch((e) => {
      console.error(e);
      return null;
    });
    return updateShellEnvWith(SHELL_ENV_FROM_HTML, envOverrides);
  };

function updateShellEnvWith(...data: unknown[]) {
  const newEnv = Object.assign(
    {},
    window.SHELL_ENV ?? {},
    ...data.filter((x) => x != null)
  );
  window.SHELL_ENV = newEnv;
  // XXX: Hackily update the SHELL_ENV const scoped variable
  Object.assign(SHELL_ENV, newEnv);
  return window.SHELL_ENV;
}

/**
 * Loads the environment from a URL returning the new environment
 * @param url the URL to fetch the environment from
 */
async function loadEnvFromURL(url: string): Promise<unknown | null> {
  try {
    const response = await fetch(url);
    if (response.status === 200) {
      return response.json();
    } else {
      return null;
    }
  } catch (e) {
    console.error(e);
    return null;
  }
}

export const CONFIG_NAME = SHELL_ENV_FROM_HTML.CONFIG_NAME;
const ENV = CONFIG_NAME.split(".")[1] as string;
export { ENV };
